import { useMemo } from 'react';
import styled from 'styled-components';
import {
  Heading,
  TopPathChartHeader,
  TopPathChartRow,
  VerticalDivider,
} from './TopPathsChartUI';
import BuilderInfo from '../../BuilderInfo';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { ChartMode } from '../types';
import ChartWrapper from '../ChartWrapper';
import EntityInfoPopover from '../../EntityInfoPopover';
import ThinDivider from '../../ThinDivider';
import BadgeRound from '../../BadgeRound';
import { ChartContextProvider } from '../ChartContext';
import { useAppSelector } from '../../../redux/hooks';
import { analysisSelector } from '../../../redux/selectors/analyses';
import { assertNotNil } from '../../../utils/types';

interface IProps {
  transientFunnelAnalysisId: string;
  rankLabel: string;
  humanLabel: string;
  label: string;
  showChartDivider: boolean;
  mode: ChartMode;
}

// Special container to make chart responsive
const ChartContainer = styled.div`
  display: flex;
  min-height: 200px;
`;
const RankLabel = styled(BadgeRound)`
  align-self: center;
`;

const TopPathFunnelChart = ({
  transientFunnelAnalysisId,
  rankLabel,
  humanLabel,
  label,
  showChartDivider,
  mode,
}: IProps) => {
  const analysisMemoizedSelector = useMemo(analysisSelector, []);
  const transientAnalysis = useAppSelector((state) =>
    analysisMemoizedSelector(state, transientFunnelAnalysisId),
  );
  assertNotNil(transientAnalysis);
  const { chartData } = transientAnalysis;
  assertNotNil(chartData);
  const isCompactMode = mode === ChartMode.Compact;

  return (
    <ChartContextProvider mode={mode} analysis={transientAnalysis}>
      {showChartDivider && <ThinDivider layout="horizontal" size="full" />}
      <TopPathChartRow>
        <RankLabel size="regular">{rankLabel}</RankLabel>
        <TopPathChartHeader>
          <Heading>
            <strong>{label}</strong> - Funnel Conversion:{' '}
            <strong>{humanLabel}</strong>
          </Heading>
          {!isCompactMode && (
            <EntityInfoPopover closeOnAncestorScroll={true}>
              <BuilderInfo
                kind="report"
                analysisId={transientFunnelAnalysisId}
                queryType={QueryType.Funnel}
                chartData={chartData}
              />
            </EntityInfoPopover>
          )}
        </TopPathChartHeader>
        <VerticalDivider />
        <ChartContainer>
          <ChartWrapper analysisId={transientFunnelAnalysisId} mode={mode} />
        </ChartContainer>
      </TopPathChartRow>
    </ChartContextProvider>
  );
};

export default TopPathFunnelChart;
